import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { SlOptions } from "react-icons/sl";
import { MdFavorite } from "react-icons/md";
import OptionsList from "./OptionsList";
import { getConversationsGroupe } from "./off-canvas-list-messages";
import { getCachedList, cacheList } from "../../services/messageCache";
import { DisplayDiscussionForMiniScreenContext } from "../../contexts/DisplayDiscussionForMiniScreenContext";
import { WorkspaceContext } from "../../contexts/WorkspaceContext";
import { AuthContext } from "../../contexts/AuthContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { stripAccents } from "../../utils/stripAccents";
import Person from "../Person";
import { socket } from "../../services/socket";
import { timeElapsed } from "../../services/getTimeSended";
import { includesId } from "../../services/idUtils";
import { CritereFiltreContext } from "../../contexts/CriteresFiltreContext";

export default function OffCanvasMessagesGroupe() {
  const { setDisplayDiscussionForMiniScreen, setCrew, setActiveRoomIdGroupe, typingGroupeMap } =
    useContext(DisplayDiscussionForMiniScreenContext);
  const { user } = useContext(AuthContext);
  const { currentWorkspace } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?._id;
  const { critereFiltre, searchText } = useContext(CritereFiltreContext);
  const [visibleIndex, setVisibleIndex] = useState(null);
  const [groupes, setGroupes] = useState([]);
  const { theme } = useContext(ThemeContext);

  const backgroundColor = useMemo(
    () => (theme === "dark" ? "#504c4ca8" : "#0067ff38"),
    [theme]
  );

  const groupesParFiltre = useMemo(() => {
    let filtered = groupes;
    if (critereFiltre === "Messages non lus") {
      filtered = filtered.filter((g) => !includesId(g.readBy, user.userId));
    } else if (critereFiltre !== "Tous les messages") {
      filtered = filtered.filter((g) => includesId(g.addFavoriteBy, user.userId));
    }
    if (searchText) {
      const term = stripAccents(searchText);
      filtered = filtered.filter((g) =>
        stripAccents(g.groupeInfo?.nom).includes(term)
      );
    }
    return filtered;
  }, [groupes, critereFiltre, searchText, user.userId]);

  const toggleOptionsList = (idx) => {
    setVisibleIndex(visibleIndex === idx ? null : idx);
  };

  const handleDeleteUpdate = useCallback(({ roomId, lastMessage }) => {
    setGroupes((prev) =>
      prev.map((conv) =>
        conv.roomId === roomId
          ? { ...conv, lastMessage: lastMessage || {} }
          : conv
      )
    );
  }, []);

  const handleFavoriteUpdate = useCallback(
    ({ roomId, added }) => {
      setGroupes((prev) =>
        prev.map((g) => {
          if (g.roomId !== roomId) return g;
          const favs = g.addFavoriteBy || [];
          return {
            ...g,
            addFavoriteBy: added
              ? [...favs, user.userId]
              : favs.filter((id) => id !== user.userId),
          };
        })
      );
    },
    [user.userId]
  );

  useEffect(() => {
    // Cache d'abord, puis serveur
    getCachedList(`conversations_groupe_${workspaceId}`).then((cached) => {
      if (cached) setGroupes(cached);
    });
    getConversationsGroupe(user, (freshGroupes) => {
      setGroupes(freshGroupes);
      cacheList(`conversations_groupe_${workspaceId}`, freshGroupes);
    }, workspaceId);

    socket.on("voici_ma_liste_de_conversation_groupe", (convs) => {
      setGroupes(convs);
      cacheList(`conversations_groupe_${workspaceId}`, convs);
    });

    socket.on(
      "message_groupe_ouvert_par_le_destinataire_dans_le_off_canvas",
      (canalUpdated) => {
        setGroupes((prev) => {
          const filtered = prev.filter(
            (canal) => canal._id !== canalUpdated._id
          );
          return [...filtered, canalUpdated].sort(
            (a, b) => (b.lastMessage?.timeUnix || 0) - (a.lastMessage?.timeUnix || 0)
          );
        });
      }
    );

    socket.on(
      "mettre_a_jour_le_dernier_message_groupe_sur_le_offcanvas",
      ({ canal }) => {
        if (!canal) return;
        setGroupes((prev) => {
          const interlocuteurs = (canal.membres || []).filter(
            (m) => m.userId !== user.userId
          );
          const updated = {
            _id: canal.canalId || canal._id,
            membres: canal.membres,
            roomId: canal.roomId,
            lastMessage: canal.lastMessage,
            readBy: canal.readBy || [],
            addFavoriteBy: canal.addFavoriteBy || [],
            interlocuteurs,
            groupeInfo: canal.groupeInfo || {},
            mutedMembers: canal.mutedMembers || [],
            membresJoinedAt: canal.membresJoinedAt || {},
            conversation: canal.conversation || "groupe",
          };
          const filtered = prev.filter((g) => g.roomId !== canal.roomId);
          return [...filtered, updated].sort(
            (a, b) => (b.lastMessage?.timeUnix || 0) - (a.lastMessage?.timeUnix || 0)
          );
        });
      }
    );

    socket.on("insertion_au_groupe", () => {
      getConversationsGroupe(user, setGroupes, workspaceId);
    });

    socket.on("membre_removed_from_groupe", ({ removedMembreId }) => {
      if (removedMembreId === user.userId) {
        getConversationsGroupe(user, setGroupes, workspaceId);
      }
    });
    socket.on("membre_added_to_groupe", ({ newMembreId }) => {
      if (newMembreId === user.userId) {
        getConversationsGroupe(user, setGroupes, workspaceId);
      }
    });
    socket.on("groupe_info_updated", ({ roomId, groupe }) => {
      setGroupes((prev) =>
        prev.map((g) =>
          g.roomId === roomId
            ? { ...g, groupeInfo: groupe.groupeInfo }
            : g
        )
      );
    });

    socket.on("dernier_message_apres_suppression", handleDeleteUpdate);

    socket.on("channel_added_with_success", handleFavoriteUpdate);
    socket.on("channel_removed_with_success", handleFavoriteUpdate);

    return () => {
      socket.off("voici_ma_liste_de_conversation_groupe");
      socket.off("mettre_a_jour_le_dernier_message_groupe_sur_le_offcanvas");
      socket.off("insertion_au_groupe");
      socket.off("membre_removed_from_groupe");
      socket.off("membre_added_to_groupe");
      socket.off(
        "message_groupe_ouvert_par_le_destinataire_dans_le_off_canvas"
      );
      socket.off("groupe_info_updated");
      socket.off("dernier_message_apres_suppression", handleDeleteUpdate);
      socket.off("channel_added_with_success", handleFavoriteUpdate);
      socket.off("channel_removed_with_success", handleFavoriteUpdate);
    };
  }, [user, handleFavoriteUpdate, handleDeleteUpdate, workspaceId]);

  return (
    <ul className="tyn-aside-list">
      {groupesParFiltre.map((groupe, idx) => (
          <li
            className={`tyn-aside-item js-toggle-main ${
              !includesId(groupe.readBy, user.userId) ? "unread" : ""
            } `}
            style={{
              backgroundColor: !includesId(groupe.readBy, user.userId)
                ? backgroundColor
                : null,
            }}
            key={groupe.roomId}
          >
            <div className="tyn-media-group">
              <div className="tyn-media tyn-size-lg">
                <Person
                  person={{
                    image: groupe.groupeInfo.imageUrl || "",
                    nom: groupe.groupeInfo.nom[1],
                    prenom: groupe.groupeInfo.nom,
                    userId: groupe._id.toString(),
                    colors: groupe.groupeInfo.color,
                  }}
                />
              </div>
              <div
                className="tyn-media-col"
                onClick={() => {
                  setCrew({
                    membres: groupe.membres,
                    interlocuteurs: groupe.interlocuteurs,
                    id: groupe._id.toString(),
                    roomId: groupe.roomId,
                    ...groupe.groupeInfo,
                    membresIds: groupe.membres.map((obj) => obj.userId),
                    mutedMembers: groupe.mutedMembers || [],
                  });
                  const key = workspaceId
                    ? `${workspaceId}:${groupe.roomId}`
                    : groupe.roomId;
                  setActiveRoomIdGroupe(key);
                  setDisplayDiscussionForMiniScreen(true);
                  socket.emit(
                    "je_declare_tous_les_messages_groupe_de_cette_discussion_ouvert",
                    {
                      destinataire: user.userId,
                      roomId: groupe.roomId,
                      workspaceId,
                    }
                  );
                  socket.emit("vaChercherLaConversation", groupe.roomId, workspaceId);
                }}
              >
                <div className="tyn-media-row">
                  <h6 className="name">
                    {groupe.groupeInfo.nom}
                  </h6>
                  {includesId(groupe.addFavoriteBy, user.userId) && (
                    <MdFavorite
                      className="text-danger"
                      style={{ fontSize: "12px", marginLeft: "4px", flexShrink: 0 }}
                    />
                  )}
                  {(typingGroupeMap[groupe.roomId] || []).length > 0 &&
                    (typingGroupeMap[groupe.roomId] || []).map((writter, idx) => (
                      <span
                        key={idx}
                        className={`meta typing text-primary ${
                          writter ? "my-text-writting-alternate-infinite" : ""
                        }`}
                      >
                        {writter.prenom + " écrit..."}
                      </span>
                    ))}
                </div>
                <div className="tyn-media-row has-dot-sap">
                  <p className="content">
                    {groupe.lastMessage?.nature === "code_meeting"
                      ? "Invitation reunion"
                      : groupe.lastMessage?.contenu || "Aucun message"}
                  </p>
                  {groupe.lastMessage?.contenu ? (
                    <span className="meta text-primary">
                      {timeElapsed(groupe.lastMessage.timeUnix)}
                    </span>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              <div className="tyn-media-option tyn-aside-item-option">
                <ul className="tyn-media-option-list">
                  <li className="dropdown">
                    <div
                      className="btn btn-icon btn-white btn-pill dropdown-toggle"
                      data-bs-toggle="dropdown"
                      data-bs-offset="0,0"
                      data-bs-auto-close="outside"
                      onClick={() => {
                        toggleOptionsList(idx);
                      }}
                    >
                      <SlOptions style={{ cursor: "pointer" }} />
                    </div>
                    {visibleIndex === idx && (
                      <OptionsList
                        roomId={groupe.roomId}
                        addFavoriteBy={groupe.addFavoriteBy}
                        setDisplayDiscussionForMiniScreen={
                          setDisplayDiscussionForMiniScreen
                        }
                        groupe={groupe}
                        isGroupe={true}
                        interlocuteur={groupe.interlocuteurs}
                        display={true}
                      />
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </li>
        ))}
    </ul>
  );
}
